import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge, type BadgeProps } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { SponsoredAd } from "@shared/schema";
import { useToast } from "@/hooks/use-toast";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { GameSponsorForm } from "./GameSponsorForm";
import { Dispatch, SetStateAction } from "react";
import { motion } from "framer-motion";
import { PlusCircle, Pause, Play, Loader2, Terminal, Trash2, Megaphone } from "lucide-react";
import { Alert, AlertTitle, AlertDescription } from "./ui/alert";

interface MySponsorshipsManagerProps {
    isCreateOpen: boolean;
    setCreateOpen: Dispatch<SetStateAction<boolean>>;
}

const adStatusConfig: Record<string, { text: string; variant: BadgeProps["variant"]; className: string }> = {
    active: { text: "Active", variant: "default", className: "bg-green-500/20 text-green-400 border-green-500/30 hover:bg-green-500/30" },
    paused: { text: "Paused", variant: "secondary", className: "" },
    depleted: { text: "Depleted", variant: "destructive", className: "" },
    inactive: { text: "Inactive", variant: "outline", className: "" },
};

const getAdStatus = (ad: SponsoredAd) => {
    if (ad.isPaused) return adStatusConfig.paused;
    if (ad.remainingBudget <= 0) return adStatusConfig.depleted;
    if (ad.isActive) return adStatusConfig.active;
    return adStatusConfig.inactive;
};

const tableVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.05 } },
};

const rowVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: { opacity: 1, y: 0 },
};

const EmptyState = ({ onOpenCreate }: { onOpenCreate: () => void }) => (
    <TableRow>
        <TableCell colSpan={5}>
            <div className="flex flex-col items-center justify-center text-center py-16 px-6 bg-background rounded-b-lg">
                <div className="p-4 bg-muted/50 rounded-full mb-4 border">
                    <Megaphone className="h-8 w-8 text-muted-foreground" />
                </div>
                <h3 className="text-xl font-semibold text-foreground">No Sponsorships Yet</h3>
                <p className="text-sm text-muted-foreground mt-2 mb-6 max-w-sm mx-auto">
                    Ready to boost your game's visibility? Create your first sponsorship campaign and start attracting new players today.
                </p>
                <Button onClick={onOpenCreate}>
                    <PlusCircle className="mr-2 h-4 w-4" />
                    Create Your First Sponsorship
                </Button>
            </div>
        </TableCell>
    </TableRow>
);

export default function MySponsorshipsManager({ isCreateOpen, setCreateOpen }: MySponsorshipsManagerProps) {
    const queryClient = useQueryClient();
    const { toast } = useToast();

    const { data: myAds, isLoading, error } = useQuery<SponsoredAd[], Error>({ 
        queryKey: ["my-sponsored-ads"],
        queryFn: () => apiRequest("GET", "/api/sponsored-ads/my-ads").then(res => res.json()),
    });

    const pauseMutation = useMutation<SponsoredAd, Error, string>({
        mutationFn: (adId) => apiRequest("PUT", `/api/sponsored-ads/${adId}/pause`).then(res => res.json()),
        onSuccess: () => {
            toast({ title: "Ad Paused", description: "Your ad campaign has been paused." });
            queryClient.invalidateQueries({ queryKey: ["my-sponsored-ads"] });
        },
        onError: (err: any) => {
            const message = err.response?.data?.message || err.message;
            toast({ title: "Error Pausing Ad", description: message, variant: "destructive" });
        }
    });

    const resumeMutation = useMutation<SponsoredAd, Error, string>({
        mutationFn: (adId) => apiRequest("PUT", `/api/sponsored-ads/${adId}/resume`).then(res => res.json()),
        onSuccess: () => {
            toast({ title: "Ad Resumed", description: "Your ad campaign is now active." });
            queryClient.invalidateQueries({ queryKey: ["my-sponsored-ads"] });
        },
        onError: (err: any) => {
            const message = err.response?.data?.message || err.message;
            toast({ title: "Error Resuming Ad", description: message, variant: "destructive" });
        }
    });

    const deleteMutation = useMutation<{ message: string }, Error, string>({
        mutationFn: (adId) => apiRequest("DELETE", `/api/sponsored-ads/${adId}`).then(res => res.json()),
        onSuccess: (data) => {
            toast({ title: "Ad Deleted", description: data.message });
            queryClient.invalidateQueries({ queryKey: ["my-sponsored-ads"] });
            queryClient.invalidateQueries({ queryKey: ["me"] }); // Invalidate user data to reflect refunded tokens
        },
        onError: (err: any) => {
            const message = err.response?.data?.message || err.message;
            toast({ title: "Error Deleting Ad", description: message, variant: "destructive" });
        }
    });

    const handleCreationSuccess = () => {
        setCreateOpen(false);
        queryClient.invalidateQueries({ queryKey: ["my-sponsored-ads"] });
        queryClient.invalidateQueries({ queryKey: ["me"] });
    };

    const renderContent = () => {
        if (isLoading) {
            return (
                <TableBody>
                    {Array.from({ length: 3 }).map((_, i) => (
                        <TableRow key={i}>
                            <TableCell className="pl-6">
                                <div className="flex items-center gap-4">
                                    <Skeleton className="h-12 w-12 rounded-md" />
                                    <Skeleton className="h-4 w-40" />
                                </div>
                            </TableCell>
                            <TableCell><Skeleton className="h-4 w-20" /></TableCell>
                            <TableCell><Skeleton className="h-6 w-full" /></TableCell>
                            <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                            <TableCell className="pr-6">
                                <div className="flex items-center justify-.end">
                                    <Skeleton className="h-9 w-24" />
                                </div>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            );
        }

        if (error) {
             return (
                <TableBody>
                    <TableRow>
                        <TableCell colSpan={5}>
                            <div className="p-6">
                                <Alert variant="destructive">
                                    <Terminal className="h-4 w-4" />
                                    <AlertTitle>Failed to Load Sponsorships</AlertTitle>
                                    <AlertDescription>{error.message}</AlertDescription>
                                </Alert>
                            </div>
                        </TableCell>
                    </TableRow>
                </TableBody>
             );
        }

        if (!myAds || myAds.length === 0) {
            return (
                <TableBody>
                    <EmptyState onOpenCreate={() => setCreateOpen(true)} />
                </TableBody>
            );
        }

        return (
            <motion.tbody variants={tableVariants} initial="hidden" animate="visible">
                {myAds.map(ad => {
                    const status = getAdStatus(ad);
                    const budgetProgress = ad.totalBudget > 0 ? (ad.remainingBudget / ad.totalBudget) * 100 : 0;

                    return (
                        <motion.tr key={ad.id} variants={rowVariants} className="hover:bg-muted/50">
                            <TableCell className="pl-6">
                                <div className="flex items-center gap-4">
                                    <img src={ad.gameThumbnailUrl} alt={ad.gameName} className="h-12 w-12 rounded-md object-cover" />
                                    <span className="font-medium text-foreground">{ad.gameName}</span>
                                </div>
                            </TableCell>
                            <TableCell>
                                <Badge variant={status.variant} className={status.className}>{status.text}</Badge>
                            </TableCell>
                            <TableCell>
                                <div className="flex flex-col gap-1.5">
                                    <Progress value={budgetProgress} className="h-2" />
                                    <span className="text-xs text-muted-foreground">
                                        {ad.remainingBudget.toLocaleString()} / {ad.totalBudget.toLocaleString()} Tokens
                                    </span>
                                </div>
                            </TableCell>
                            <TableCell className="text-muted-foreground text-sm">{new Date(ad.createdAt).toLocaleDateString()}</TableCell>
                            <TableCell className="pr-6">
                                <div className="flex items-center justify-end">
                                    {status.text === "Active" && (
                                        <Button variant="ghost" size="icon" onClick={() => pauseMutation.mutate(ad.id)} disabled={pauseMutation.isPending && pauseMutation.variables === ad.id}>
                                            {pauseMutation.isPending && pauseMutation.variables === ad.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Pause className="w-4 h-4" />} 
                                        </Button>
                                    )}
                                    {status.text === "Paused" && (
                                        <Button variant="ghost" size="icon" onClick={() => resumeMutation.mutate(ad.id)} disabled={resumeMutation.isPending && resumeMutation.variables === ad.id}>
                                            {resumeMutation.isPending && resumeMutation.variables === ad.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
                                        </Button>
                                    )}
                                    <AlertDialog>
                                        <AlertDialogTrigger asChild>
                                            <Button variant="ghost" size="icon" className="text-red-500 hover:text-red-600">
                                                 <Trash2 className="w-4 h-4" />
                                            </Button>
                                        </AlertDialogTrigger>
                                        <AlertDialogContent>
                                            <AlertDialogHeader>
                                                <AlertDialogTitle>Are you sure?</AlertDialogTitle>
                                                <AlertDialogDescription>
                                                    This action cannot be undone. This will permanently delete your sponsorship and refund any remaining budget to your account.
                                                </AlertDialogDescription>
                                            </AlertDialogHeader>
                                            <AlertDialogFooter>
                                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                                <AlertDialogAction
                                                    onClick={() => deleteMutation.mutate(ad.id)}
                                                    disabled={deleteMutation.isPending && deleteMutation.variables === ad.id}
                                                    className="bg-red-500 hover:bg-red-600"
                                                >
                                                    {deleteMutation.isPending && deleteMutation.variables === ad.id ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                                                    Delete Sponsorship
                                                </AlertDialogAction>
                                            </AlertDialogFooter>
                                        </AlertDialogContent>
                                    </AlertDialog>
                                </div>
                            </TableCell>
                        </motion.tr>
                    )
                })}
            </motion.tbody>
        );
    };

    return (
        <Card className="overflow-hidden">
            <CardHeader className="flex flex-row items-center justify-between">
                <div>
                    <CardTitle>My Sponsorships</CardTitle>
                    <CardDescription>Manage your active and past game sponsorships.</CardDescription>
                </div>
                <Dialog open={isCreateOpen} onOpenChange={setCreateOpen}>
                    <DialogTrigger asChild>
                        <Button>
                           <PlusCircle className="mr-2 h-4 w-4" />
                            Create New
                        </Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-2xl">
                        <GameSponsorForm onSuccess={handleCreationSuccess} />
                    </DialogContent>
                </Dialog>
            </CardHeader>
            <CardContent className="p-0">
                <div className="overflow-x-auto">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead className="pl-6">Game</TableHead>
                                <TableHead>Status</TableHead>
                                <TableHead className="w-[25%]">Budget</TableHead>
                                <TableHead>Created</TableHead>
                                <TableHead className="text-right pr-6">Actions</TableHead>
                            </TableRow>
                        </TableHeader>
                        {renderContent()}
                    </Table>
                </div>
            </CardContent>
        </Card>
    );
}
